/**
 * animationSnapshotProvider.ts
 *
 * Provides the data generation strategy for the Keyword Trends Animation page.
 * Production provider: createApiSnapshotProvider — backed by
 * GET /api/cluster/{client}/{anchor}/history
 */

import { clusterApi } from "./api";
import { ClusterCenter, KeywordWithMetadata, TrendData } from "../types";

// ---------------------------------------------------------------------------
// Shared types (consumed by the animation page and the dataset table)
// ---------------------------------------------------------------------------

/** A single point-in-time snapshot of all keyword metrics on a given date. */
export type Snapshot = {
  /** ISO date string, e.g. "2024-03-18". */
  date: string;
  data: KeywordWithMetadata[];
  /**
   * Volume-weighted cluster center for this frame, computed client-side from
   * `data` (the main chart gets its center from the backend, but the animation
   * recomputes per frame — mirroring how keyword trends are computed here). Its
   * trend is the between-frames delta of the center's weighted rank; null on
   * frame 0.
   */
  center: ClusterCenter | null;
};

/** Input required to generate a snapshot timeline. */
export type GenerateSnapshotsInput = {
  /** Seed keyword dataset from which the first snapshot is built. */
  baseData: KeywordWithMetadata[];
  /** Inclusive start of the date range. */
  startDate: Date;
  /** Inclusive end of the date range. */
  endDate: Date;
};

/** Result returned after generating a snapshot timeline. */
export type GenerateSnapshotsResult = {
  /** Ordered list of snapshots — one per update date in the selected range. */
  snapshots: Snapshot[];
  /**
   * Number of Mondays found in the range.
   * The animation page uses this to decide whether playback should be enabled
   * (requires at least 2 weekly data points for meaningful animation).
   */
  mondaysInRange: number;
};

/**
 * Contract that any snapshot provider must satisfy.
 * generateSnapshots is async so providers can call remote APIs.
 */
export interface AnimationSnapshotProvider {
  generateSnapshots: (input: GenerateSnapshotsInput) => Promise<GenerateSnapshotsResult>;
}

// ---------------------------------------------------------------------------
// Real API provider — backed by GET /api/cluster/{client}/{anchor}/history
// ---------------------------------------------------------------------------

// Rank 0 from DataForSEO means "not ranked in the top 100". For delta math we treat it as worse than rank 100 so a drop to unranked counts as a decline rather than an improvement.
const UNRANKED_RANK = 105; // changing this should also change the value in BE
const normalizeRank = (rank: number): number => (rank === 0 ? UNRANKED_RANK : rank);

// Unranked value used in the center's POSITION/size average (mirrors the
// backend UNRANKED_RANK_OVERVIEW). Distinct from UNRANKED_RANK (105), which is
// only for the trend delta — see the cluster-center marker docs.
const UNRANKED_RANK_OVERVIEW = 100;

/**
 * Volume-weighted center position/size for one frame's keywords. Returns null
 * for an empty frame. Mirrors the backend compute_cluster_center: difficulty
 * (X) volume-weighted, volume (Y) a plain average, ranking (size) volume-
 * weighted with unranked counted as 100. Falls back to unweighted means if the
 * frame's total volume is 0 (defensive).
 */
const computeCenterPosition = (
  data: KeywordWithMetadata[],
): { difficulty: number; volume: number; ranking: number } | null => {
  if (data.length === 0) return null;
  const volumes = data.map((d) => Math.max(0, d.volume || 0));
  const difficulties = data.map((d) => d.difficulty || 0);
  const ranks = data.map((d) =>
    d.ranking && d.ranking > 0 ? d.ranking : UNRANKED_RANK_OVERVIEW,
  );
  const totalVolume = volumes.reduce((a, b) => a + b, 0);
  const count = data.length;
  const avgVolume = totalVolume / count;

  let wDifficulty: number;
  let wRanking: number;
  if (totalVolume > 0) {
    wDifficulty =
      difficulties.reduce((s, d, i) => s + volumes[i] * d, 0) / totalVolume;
    wRanking = ranks.reduce((s, r, i) => s + volumes[i] * r, 0) / totalVolume;
  } else {
    wDifficulty = difficulties.reduce((a, b) => a + b, 0) / count;
    wRanking = ranks.reduce((a, b) => a + b, 0) / count;
  }
  return { difficulty: wDifficulty, volume: avgVolume, ranking: Math.round(wRanking) };
};

/**
 * The center's volume-weighted rank for the trend delta (unranked = 105, to
 * match keyword deltas). null for an empty frame.
 */
const computeCenterTrendRank = (data: KeywordWithMetadata[]): number | null => {
  if (data.length === 0) return null;
  const volumes = data.map((d) => Math.max(0, d.volume || 0));
  const ranks = data.map((d) => normalizeRank(d.ranking));
  const totalVolume = volumes.reduce((a, b) => a + b, 0);
  if (totalVolume > 0) {
    return ranks.reduce((s, r, i) => s + volumes[i] * r, 0) / totalVolume;
  }
  return ranks.reduce((a, b) => a + b, 0) / data.length;
};

/** Formats a Date object as an ISO date string ("YYYY-MM-DD"). */
const toIsoDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Factory that returns an AnimationSnapshotProvider backed by real DB data.
 *
 * Trend strategy — between-frames rank delta:
 *   Frame 0   → trend = null  (no prior frame to compare against)
 *   Frame N>0 → trend = normalize(prevRank) − normalize(currRank)
 *               Positive = improved (moved closer to #1)
 *               Negative = worsened
 *   Rank 0 ("not ranked in top 100") is normalized to UNRANKED_RANK (105) so a
 *   drop from a real rank to unranked counts as a decline rather than an improvement.
 * The same delta is assigned to week / month / life so the bubble chart's
 * default trendPeriod ("life") picks it up for colour coding.
 *
 * Forward-fill:
 *   Keywords absent from a snapshot frame (added after the frame date) are
 *   simply omitted.  Keywords present but with is_forward_filled = true have
 *   the same metrics as their previous real fetch; the isForwardFilled flag
 *   is passed through to KeywordWithMetadata so AnimationDatasetTable can
 *   display a "←" indicator.
 */
export const createApiSnapshotProvider = (
  client: string,
  anchorKeyword: string,
  languageCode: string = "en",
  locationCode: number = 2840,
): AnimationSnapshotProvider => ({
  generateSnapshots: async ({ startDate, endDate }) => {
    const dateFrom = toIsoDate(startDate);
    const dateTo = toIsoDate(endDate);

    // Fetch real historical data from the backend.
    const response = await clusterApi.getKeywordHistory(
      client,
      anchorKeyword,
      dateFrom,
      dateTo,
      languageCode,
      locationCode,
    );

    const anchorLower = anchorKeyword.toLowerCase();

    // prevRankMap tracks each keyword's rank from the previous frame so we can
    // compute the between-frames rank delta for trend colouring.
    const prevRankMap = new Map<string, number>();
    // The previous frame's center weighted-rank, for the center's between-frames
    // trend delta (null before the first frame).
    let prevCenterRank: number | null = null;

    const snapshots: Snapshot[] = response.snapshots.map((snap) => {
      const data: KeywordWithMetadata[] = snap.keywords.map((item) => {
        // Between-frames trend: positive means rank improved (closer to #1).
        const prevRank = prevRankMap.get(item.keyword) ?? null;
        const delta =
          prevRank !== null
            ? normalizeRank(prevRank) - normalizeRank(item.ranking)
            : null;
        const trend: TrendData = { week: delta, month: delta, life: delta };

        return {
          keyword: item.keyword,
          volume: item.volume,
          difficulty: item.difficulty,
          ranking: item.ranking,
          trend,
          isAnchor: item.keyword === anchorLower,
          parentAnchor: anchorKeyword,
          ranking_url: item.ranking_url ?? undefined,
          isForwardFilled: item.is_forward_filled,
          isRankForwardFilled: item.rank_is_forward_filled ?? false,
        };
      });

      // Update rank map with this frame's values so the next frame can compute deltas.
      data.forEach((kw) => prevRankMap.set(kw.keyword, kw.ranking));

      // Per-frame cluster center. Its trend is the between-frames delta of the
      // weighted rank (positive = improved), null on the first frame — the same
      // convention as the keyword trends above.
      const position = computeCenterPosition(data);
      const centerRank = computeCenterTrendRank(data);
      const centerDelta =
        prevCenterRank !== null && centerRank !== null
          ? prevCenterRank - centerRank
          : null;
      const center: ClusterCenter | null = position
        ? {
            keyword: "Cluster Weighted Average",
            volume: position.volume,
            difficulty: position.difficulty,
            ranking: position.ranking,
            trend: { week: centerDelta, month: centerDelta, life: centerDelta },
            isCenter: true,
          }
        : null;
      prevCenterRank = centerRank;

      return { date: snap.date, data, center };
    });

    // Any 2+ real snapshots enable animation — no Monday-gate needed for real data.
    return { snapshots, mondaysInRange: snapshots.length };
  },
});



// ---------------------------------------------------------------------------
// DEMO ONLY — random drift simulation (uncomment to test without a backend)
// ---------------------------------------------------------------------------
/*
const CHANGE_INTENSITY = 0.3;

const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateInput = (value: string): Date => new Date(`${value}T00:00:00`);

const isMonday = (date: Date): boolean => date.getDay() === 1;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const driftValue = (currentValue: number): number => {
  const direction = Math.random() < 0.5 ? -1 : 1;
  const randomPercent = Math.random() * CHANGE_INTENSITY;
  return Math.round(currentValue * (1 + direction * randomPercent));
};

const driftTrend = (trend: TrendData): TrendData => ({
  week: clamp(driftValue(trend.week ?? 0), -100, 100),
  month: clamp(driftValue(trend.month ?? 0), -100, 100),
  life: clamp(driftValue(trend.life ?? 0), -100, 100),
});

const driftKeywordData = (currentData: KeywordWithMetadata[]): KeywordWithMetadata[] => {
  return currentData.map((keyword) => ({
    ...keyword,
    volume: Math.max(0, driftValue(keyword.volume)),
    difficulty: clamp(driftValue(keyword.difficulty), 0, 100),
    ranking: clamp(driftValue(keyword.ranking), 1, 100),
    trend: driftTrend(keyword.trend),
  }));
};

const getUpdateDates = (start: Date, end: Date): string[] => {
  const dates: string[] = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    const day = cursor.getDay();
    const isUpdateDay = day === 1 || (day === 4 && Math.random() < 0.4);

    if (isUpdateDay) {
      dates.push(formatDateForInput(cursor));
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
};

const randomAnimationSnapshotProvider: AnimationSnapshotProvider = {
  generateSnapshots: async ({ baseData, startDate, endDate }) => {
    const updateDates = getUpdateDates(startDate, endDate);

    const mondaysInRange = updateDates.filter((dateStr) =>
      isMonday(parseDateInput(dateStr))
    ).length;

    const snapshots: Snapshot[] = [];
    let current = baseData.map((item) => ({ ...item }));

    updateDates.forEach((date, index) => {
      if (index > 0) {
        current = driftKeywordData(current);
      }
      snapshots.push({ date, data: current.map((item) => ({ ...item })) });
    });

    return { snapshots, mondaysInRange };
  },
};

// DEMO ONLY — uncomment together with the block above
export const animationSnapshotProvider: AnimationSnapshotProvider =
  randomAnimationSnapshotProvider;
*/

